import { useState, useMemo, useEffect } from "react";
import {
  FaSearch,
  FaPlus,
  FaEye,
  FaEdit,
  FaCheck,
  FaTimes,
  FaChartLine,
  FaBookmark,
  FaUserShield,
  FaMapMarkerAlt,
  FaUser,
  FaFileAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  authUserData,
  deleteDashboardsForApi,
  getAllLayoutsByRoleForApi,
  getApiQueriesList,
  getThemeURL,
  statusUpdateForApi,
} from "../../../Services/EndPoints";
import { getData, postDataWithBody } from "../../../Services/Services";
import Loader from "react-spinner-loader";
import { toast } from "react-toastify";

const statusStyles = {
  Published: { backgroundColor: "#dcfce7", color: "#15803d", icon: "✓" },
  Saved: { backgroundColor: "#fef9c3", color: "#92400e", icon: "💾" },
  "In Progress": { backgroundColor: "#e0f2fe", color: "#1e3a8a", icon: "🔄" },
  Draft: { backgroundColor: "#f3f4f6", color: "#6b7280", icon: "📝" },
};

// Role-based access configuration
const ROLES = {
  SUPER_ADMIN: "Super Admin",
  DISTRICT: "District",
  USER: "User",
};

const Data = [];

// Users with their associated districts
const DataForDistricts = [];

const DashboardCard = ({
  dashboard,
  isSelected,
  onSelect,
  isReadOnly = false,
  isDraft = false,
  currentUserAdminRole,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [userId, setUserId] = useState(null);
  const [savedQueries, setSavedQueries] = useState([]);
  const [loader, setLoader] = useState(false);

  const [savedQueriesPrimary, setSavedQueriesPrimary] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await getData(getThemeURL);
      if (response && Array.isArray(response) && response.length > 0) {
        setUserId(response[0].id);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const isEditDisabled = () => {
    // If read-only mode, disable edit
    if (isReadOnly) return true;

    // Get dashboard's admin role from originalData or direct property
    const dashboardAdminRole =
      dashboard.originalData?.adminRole ||
      dashboard.dashboardAdminRole ||
      dashboard.adminRole;

    // If no admin role info available, allow edit (fallback)
    if (!dashboardAdminRole || !currentUserAdminRole) return false;

    // Only allow edit if current user's admin role matches dashboard's admin role
    return currentUserAdminRole !== dashboardAdminRole;
  };

  const editDisabled = isEditDisabled();

  useEffect(() => {
    const fetchSavedQueries = async () => {
      try {
        const response = await getData(getApiQueriesList);

        // Separate primary and secondary queries
        const primaryQueries = response
          .filter(
            (query) =>
              query.queryStage === "PRIMARY" &&
              query.activeStatus === true &&
              query.queryBuilder // **ADD THIS CHECK**
          )
          .map((item) => ({
            ...item,
            queryBuilder:
              typeof item.queryBuilder === "string"
                ? JSON.parse(item.queryBuilder)
                : item.queryBuilder,
          }));

        const savedQueries = response
          .filter(
            (query) =>
              query.queryStage === "SECONDARY" &&
              query.activeStatus === true &&
              query.queryBuilder // **ADD THIS CHECK**
          )
          .map((item) => ({
            ...item,
            queryBuilder:
              typeof item.queryBuilder === "string"
                ? JSON.parse(item.queryBuilder)
                : item.queryBuilder,
          }));

        setSavedQueriesPrimary(primaryQueries);
        setSavedQueries(savedQueries);
      } catch (err) {
        console.error("Error fetching saved queries:", err);
      }
    };

    fetchSavedQueries();
  }, []);

  const refreshDashboardData = async (layout, dashboardData) => {
    console.log("=== REFRESH DASHBOARD DATA DEBUG ===");
    console.log("Layout widgets:", layout.length);
    console.log("Dashboard bankids:", dashboardData?.bankids);
    console.log("Dashboard defaultBankId:", dashboardData?.defaultBankId);
    console.log("Dashboard branchids:", dashboardData?.branchids);
    console.log("Dashboard bankids type:", typeof dashboardData?.bankids);
    console.log(
      "savedQueries available:",
      !!savedQueries,
      "count:",
      savedQueries?.length || 0
    );
    console.log(
      "savedQueriesPrimary available:",
      !!savedQueriesPrimary,
      "count:",
      savedQueriesPrimary?.length || 0
    );

    if (!layout || !Array.isArray(layout)) return layout;

    try {
      const refreshPromises = layout.map(async (widget) => {
        try {
          // Only refresh widgets that have data sources or widget IDs
          if (!widget.dataSource && !widget.id) {
            console.log(`Skipping widget ${widget.id} - no dataSource or id`);
            return widget;
          }

          console.log(`Processing widget ${widget.id}, type: ${widget.type}`);
          console.log(`Widget dataSource:`, widget.dataSource);

          const widgetType = widget.type?.toString().trim();

          // SPECIAL HANDLING FOR MAP WIDGETS WITH MULTIPLE DATA SOURCES
          if (
            widgetType === "map" &&
            Array.isArray(widget.dataSource) &&
            widget.dataSource.length > 1
          ) {
            console.log(
              `Processing map widget with multiple data sources:`,
              widget.dataSource
            );

            // Create promises for each data source
            const dataSourcePromises = widget.dataSource.map(
              async (dataSource) => {
                console.log(`Fetching data for map data source: ${dataSource}`);

                let matchedQuery = null;

                // Find the matching query for this specific data source
                if (!savedQueries || savedQueries.length === 0) {
                  console.warn(
                    "savedQueries not loaded - attempting to refetch"
                  );

                  try {
                    const response = await getData(getApiQueriesList);

                    if (response && Array.isArray(response)) {
                      const secondaryQueries = response
                        .filter(
                          (query) =>
                            query.queryStage === "SECONDARY" &&
                            query.activeStatus === true &&
                            query.queryBuilder
                        )
                        .map((item) => ({
                          ...item,
                          queryBuilder:
                            typeof item.queryBuilder === "string"
                              ? JSON.parse(item.queryBuilder)
                              : item.queryBuilder,
                        }));

                      // FIXED: Use dashboardWidgetId for primary matching, then fall back to serviceName
                      matchedQuery = secondaryQueries.find(
                        (q) =>
                          q.dashboardWidgetId === `WIDGET_${widget.id}` &&
                          q.serviceName === dataSource &&
                          q.queryStage === "SECONDARY" &&
                          q.activeStatus === true
                      );

                      // Fallback: try matching by serviceName only if no widget ID + serviceName match
                      if (!matchedQuery) {
                        matchedQuery = secondaryQueries.find(
                          (q) =>
                            q.serviceName === dataSource &&
                            q.queryStage === "SECONDARY" &&
                            q.activeStatus === true
                        );

                        if (matchedQuery) {
                          console.log(
                            `Using fallback match for map widget ${widget.id} data source ${dataSource}`
                          );
                        }
                      }
                    } else {
                      console.error(
                        "Invalid response when refetching secondary queries"
                      );
                      return { dataSource, data: [] };
                    }
                  } catch (refetchError) {
                    console.error(
                      "Error refetching secondary queries:",
                      refetchError
                    );
                    return { dataSource, data: [] };
                  }
                } else {
                  // FIXED: Use existing secondary queries with proper widget ID matching
                  // Primary match: dashboardWidgetId AND serviceName
                  matchedQuery = savedQueries.find(
                    (q) =>
                      q.dashboardWidgetId === `WIDGET_${widget.id}` &&
                      q.serviceName === dataSource &&
                      q.queryStage === "SECONDARY" &&
                      q.activeStatus === true
                  );

                  // Fallback: try matching by serviceName only if no widget ID + serviceName match
                  if (!matchedQuery) {
                    matchedQuery = savedQueries.find(
                      (q) =>
                        q.serviceName === dataSource &&
                        q.queryStage === "SECONDARY" &&
                        q.activeStatus === true
                    );

                    if (matchedQuery) {
                      console.log(
                        `Using fallback match for map widget ${widget.id} data source ${dataSource}`
                      );
                    }
                  }
                }

                if (!matchedQuery) {
                  console.warn(
                    `No query found for map widget ${widget.id} data source: ${dataSource}`
                  );
                  return { dataSource, data: [] };
                }

                // Validate queryBuilder exists
                if (!matchedQuery.queryBuilder) {
                  console.warn(
                    `matchedQuery.queryBuilder is missing for data source ${dataSource}:`,
                    matchedQuery
                  );
                  return { dataSource, data: [] };
                }

                // Parse query object
                let queryObject;
                try {
                  queryObject =
                    typeof matchedQuery.queryBuilder === "string"
                      ? JSON.parse(matchedQuery.queryBuilder)
                      : matchedQuery.queryBuilder;
                } catch (parseError) {
                  console.error(
                    "Error parsing queryBuilder:",
                    parseError,
                    matchedQuery
                  );
                  return { dataSource, data: [] };
                }

                // Apply the same bank and branch filtering logic as other widgets
                const determineFilterFields = (queryObj) => {
                  const entity = queryObj.entity || "";
                  const tableName = entity.includes(".")
                    ? entity.split(".")[1]
                    : entity;

                  const joins = queryObj.joins || [];
                  const joinedTables = joins.map((join) => {
                    const table = join.table || join.alias || "";
                    return table.includes(".") ? table.split(".")[1] : table;
                  });

                  let bankFilterField = null;
                  let branchFilterField = null;

                  const hasBankTable =
                    tableName === "bank" || joinedTables.includes("bank");
                  const hasBranchTable =
                    tableName === "branch_details" ||
                    joinedTables.includes("branch_details");
                  const hasJoins = joins.length > 0;

                  // Determine bank filter field
                  if (tableName === "bank") {
                    bankFilterField = "bank.id";
                  } else if (hasBankTable && hasJoins) {
                    bankFilterField = "bank.id";
                  } else if (
                    !hasBankTable &&
                    tableName !== "bank" &&
                    tableName !== "branch_details"
                  ) {
                    bankFilterField = `${tableName}.bank_id`;
                  } else if (!hasBankTable) {
                    bankFilterField = `${tableName}.bank_id`;
                  }

                  // Determine branch filter field
                  if (tableName === "branch_details") {
                    branchFilterField = "branch_details.id";
                  } else if (hasBranchTable && hasJoins) {
                    branchFilterField = "branch_details.id";
                  } else if (
                    !hasBranchTable &&
                    tableName !== "bank" &&
                    tableName !== "branch_details"
                  ) {
                    branchFilterField = `${tableName}.branch_id`;
                  } else if (
                    !hasBranchTable &&
                    tableName !== "branch_details"
                  ) {
                    branchFilterField = `${tableName}.branch_id`;
                  }

                  return { bankFilterField, branchFilterField };
                };

                const { bankFilterField, branchFilterField } =
                  determineFilterFields(queryObject);

                // Build filters
                let shouldAddUserType = false;
                let bankIds = null;
                let userAdminRole = null;

                try {
                  const userResponse = await getData(getThemeURL);
                  const user = userResponse[0];
                  userAdminRole = user.role_name;

                  if (userAdminRole === "SUPER ADMIN") {
                    console.log(
                      "SA user detected - using defaultBankId from dashboard"
                    );

                    // Get defaultBankId from dashboard data parameter
                    const defaultBankId = dashboardData?.defaultBankId;

                    if (defaultBankId !== null && defaultBankId !== undefined) {
                      // Convert defaultBankId to array format
                      if (typeof defaultBankId === "string") {
                        bankIds = [parseInt(defaultBankId)];
                      } else if (typeof defaultBankId === "number") {
                        bankIds = [defaultBankId];
                      }
                    }
                  } else {
                    if (user.bank_id) {
                      bankIds = [user.bank_id];

                      if (currentUserAdminRole === "U") {
                        shouldAddUserType = true;
                      }
                    }
                  }
                } catch (error) {
                  console.error(
                    "Error fetching user profile for filters:",
                    error
                  );
                }

                // Build the enhanced query with dynamic bank filters
                const dynamicFilters = {};

                if (bankIds && bankIds.length > 0 && bankFilterField) {
                  if (bankIds.length === 1) {
                    dynamicFilters[bankFilterField] = bankIds[0];
                  } else {
                    dynamicFilters[bankFilterField] = {
                      operator: "IN",
                      value: bankIds,
                    };
                  }
                }

                // Add branch_id: null for BANK_USER type if branchFilterField exists
                if (shouldAddUserType && branchFilterField) {
                  dynamicFilters[branchFilterField] = null;
                }

                const enhancedQuery = {
                  ...queryObject,
                  filters: {
                    ...queryObject.filters,
                    ...dynamicFilters,
                  },
                };

                if (shouldAddUserType && bankIds && bankIds.length > 0) {
                  enhancedQuery.userType = "BANK_USER";
                }

                // Fetch user profile to determine auth parameters
                const userResponse = await getData(getThemeURL);
                if (
                  !userResponse ||
                  !Array.isArray(userResponse) ||
                  userResponse.length === 0
                ) {
                  console.error("Failed to fetch user profile");
                  return { dataSource, data: [] };
                }

                const user = userResponse[0];
                const roleName = user.role_name;
                const userBankId = user.bank_id;

                const authObject = {
                  instanceUrl: "/query-builder-api/execute",
                  apiType: "POST",
                };

                if (roleName === "SUPER ADMIN") {
                  authObject.type = "SA";
                  // Use defaultBankId for SA
                  const defaultBankId = dashboardData?.defaultBankId;

                  authObject.bank_id = defaultBankId
                    ? [Number(defaultBankId)]
                    : [];
                } else if (user.is_admin === true) {
                  authObject.type = "HO";
                  authObject.bank_id = userBankId ? [userBankId] : [];
                } else {
                  authObject.type = "U";
                  authObject.bank_id = userBankId ? [userBankId] : [];
                }

                // Prepare the final payload
                const payloadWithAuth = {
                  auth: authObject,
                  main: enhancedQuery,
                };

                console.log(
                  `Enhanced query for map data source ${dataSource}:`,
                  JSON.stringify(payloadWithAuth)
                );

                // Make API call for this data source
                const response = await fetch("/nifi/fetchQuery-API", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(payloadWithAuth),
                });

                if (!response.ok) {
                  throw new Error(
                    `API call failed for ${dataSource}: ${response.status} ${response.statusText}`
                  );
                }

                const result = await response.json();
                console.log(
                  `✓ API response received for map data source ${dataSource}`
                );

                console.log("Result for api call", result);
                return {
                  dataSource,
                  data: result.aggregatedData?.data || [],
                };
              }
            );

            // Wait for all data source promises to complete
            const dataSourceResults = await Promise.all(dataSourcePromises);

            // Combine results into the expected format for maps
            const combinedData = {};
            dataSourceResults.forEach((result) => {
              combinedData[result.dataSource] = result.data;
            });

            console.log(
              "Combined map data from multiple sources:",
              combinedData
            );

            // Update the map widget with combined data
            return updateWidgetWithFreshData(widget, { data: combinedData });
          }

          // EXISTING LOGIC FOR NON-MAP WIDGETS OR SINGLE DATA SOURCE MAPS
          let matchedQuery = null;

          // Handle HO_bank_list widget type with special logic
          if (widgetType === "HO_bank_list") {
            console.log(
              "Processing HO_bank_list widget - checking primary queries"
            );

            if (!savedQueriesPrimary || savedQueriesPrimary.length === 0) {
              console.warn(
                "savedQueriesPrimary not loaded - attempting to refetch for HO_bank_list"
              );

              try {
                const response = await getData(getApiQueriesList);

                if (response && Array.isArray(response)) {
                  const primaryQueries = response
                    .filter(
                      (query) =>
                        query.queryStage === "PRIMARY" &&
                        query.activeStatus === true &&
                        query.queryBuilder
                    )
                    .map((item) => ({
                      ...item,
                      queryBuilder:
                        typeof item.queryBuilder === "string"
                          ? JSON.parse(item.queryBuilder)
                          : item.queryBuilder,
                    }));

                  console.log(
                    "Refetched primary queries:",
                    primaryQueries.length
                  );

                  if (primaryQueries.length === 0) {
                    console.warn("No primary queries available after refetch");
                    return widget;
                  }

                  const serviceName = "bank_branch_details";
                  matchedQuery = primaryQueries.find(
                    (q) => q.serviceName === serviceName
                  );

                  console.log("matchedQueryDASH_HO (refetched)", matchedQuery);
                } else {
                  console.error("Invalid response when refetching queries");
                  return widget;
                }
              } catch (refetchError) {
                console.error(
                  "Error refetching queries for HO_bank_list:",
                  refetchError
                );
                return widget;
              }
            } else {
              // Use existing primary queries
              const serviceName = "bank_branch_details";
              matchedQuery = savedQueriesPrimary.find(
                (q) => q.serviceName === serviceName
              );

              console.log("matchedQueryDASH_HO (from state)", matchedQuery);
              console.log(
                "Available primary services:",
                savedQueriesPrimary.map((q) => q.serviceName)
              );
            }

            if (!matchedQuery) {
              console.warn(
                `No PRIMARY query found for HO_bank_list widget with serviceName: bank_branch_details`
              );
              return widget;
            }
          } else {
            // Handle all other widget types (including single data source maps)
            console.log(
              "Processing non-HO_bank_list widget - using secondary queries"
            );

            // For single data source maps, use the first (or only) data source
            const dataSourceToUse = Array.isArray(widget.dataSource)
              ? widget.dataSource[0]
              : widget.dataSource;

            if (!savedQueries || savedQueries.length === 0) {
              console.warn(
                "savedQueries not loaded for regular widget - attempting to refetch"
              );

              try {
                const response = await getData(getApiQueriesList);

                if (response && Array.isArray(response)) {
                  const secondaryQueries = response
                    .filter(
                      (query) =>
                        query.queryStage === "SECONDARY" &&
                        query.activeStatus === true &&
                        query.queryBuilder
                    )
                    .map((item) => ({
                      ...item,
                      queryBuilder:
                        typeof item.queryBuilder === "string"
                          ? JSON.parse(item.queryBuilder)
                          : item.queryBuilder,
                    }));

                  console.log(
                    "Refetched secondary queries:",
                    secondaryQueries.length
                  );

                  if (secondaryQueries.length === 0) {
                    console.warn(
                      "No secondary queries available after refetch"
                    );
                    return widget;
                  }

                  // Find the matching query using dashboardWidgetId and queryStage
                  matchedQuery = secondaryQueries.find(
                    (q) =>
                      q.dashboardWidgetId === `WIDGET_${widget.id}` &&
                      q.queryStage === "SECONDARY" &&
                      q.activeStatus === true
                  );

                  // Fallback: try matching by serviceName if no widget ID match
                  if (!matchedQuery && dataSourceToUse) {
                    matchedQuery = secondaryQueries.find(
                      (q) =>
                        q.serviceName === dataSourceToUse &&
                        q.queryStage === "SECONDARY" &&
                        q.activeStatus === true
                    );

                    if (matchedQuery) {
                      console.log(
                        `Using fallback match for widget ${widget.id} with serviceName: ${dataSourceToUse}`
                      );
                    }
                  }
                } else {
                  console.error(
                    "Invalid response when refetching secondary queries"
                  );
                  return widget;
                }
              } catch (refetchError) {
                console.error(
                  "Error refetching secondary queries:",
                  refetchError
                );
                return widget;
              }
            } else {
              // Use existing secondary queries
              // Find the matching query using dashboardWidgetId and queryStage
              matchedQuery = savedQueries.find(
                (q) =>
                  q.dashboardWidgetId === `WIDGET_${widget.id}` &&
                  q.queryStage === "SECONDARY" &&
                  q.activeStatus === true
              );

              // Fallback: try matching by serviceName if no widget ID match
              if (!matchedQuery && dataSourceToUse) {
                matchedQuery = savedQueries.find(
                  (q) =>
                    q.serviceName === dataSourceToUse &&
                    q.queryStage === "SECONDARY" &&
                    q.activeStatus === true
                );

                if (matchedQuery) {
                  console.log(
                    `Using fallback match for widget ${widget.id} with serviceName: ${dataSourceToUse}`
                  );
                }
              }
            }

            if (!matchedQuery) {
              console.warn(
                `No SECONDARY query found for widget ID: ${widget.id} or dataSource: ${dataSourceToUse}`
              );
              return widget;
            }
          }

          // Validate queryBuilder exists
          if (!matchedQuery.queryBuilder) {
            console.warn(
              `matchedQuery.queryBuilder is missing for widget ${widget.id}:`,
              matchedQuery
            );
            return widget;
          }

          // Parse query object
          let queryObject;
          try {
            queryObject =
              typeof matchedQuery.queryBuilder === "string"
                ? JSON.parse(matchedQuery.queryBuilder)
                : matchedQuery.queryBuilder;
          } catch (parseError) {
            console.error(
              "Error parsing queryBuilder:",
              parseError,
              matchedQuery
            );
            return widget;
          }

          // Helper function to determine both bank and branch filter fields
          const determineFilterFields = (queryObj) => {
            const entity = queryObj.entity || "";
            const tableName = entity.includes(".")
              ? entity.split(".")[1]
              : entity;

            const joins = queryObj.joins || [];
            const joinedTables = joins.map((join) => {
              const table = join.table || join.alias || "";
              return table.includes(".") ? table.split(".")[1] : table;
            });

            let bankFilterField = null;
            let branchFilterField = null;

            const hasBankTable =
              tableName === "bank" || joinedTables.includes("bank");
            const hasBranchTable =
              tableName === "branch_details" ||
              joinedTables.includes("branch_details");
            const hasJoins = joins.length > 0;

            console.log("Filter fields analysis for widget:", widget.id, {
              tableName,
              joinedTables,
              hasBankTable,
              hasBranchTable,
              hasJoins,
            });

            // Determine bank filter field
            if (tableName === "bank") {
              bankFilterField = "bank.id";
            } else if (hasBankTable && hasJoins) {
              bankFilterField = "bank.id";
            } else if (
              !hasBankTable &&
              tableName !== "bank" &&
              tableName !== "branch_details"
            ) {
              bankFilterField = `${tableName}.bank_id`;
            } else if (!hasBankTable) {
              bankFilterField = `${tableName}.bank_id`;
            }

            // Determine branch filter field
            if (tableName === "branch_details") {
              branchFilterField = "branch_details.id";
            } else if (hasBranchTable && hasJoins) {
              branchFilterField = "branch_details.id";
            } else if (
              !hasBranchTable &&
              tableName !== "bank" &&
              tableName !== "branch_details"
            ) {
              branchFilterField = `${tableName}.branch_id`;
            } else if (!hasBranchTable && tableName !== "branch_details") {
              branchFilterField = `${tableName}.branch_id`;
            }

            return { bankFilterField, branchFilterField };
          };

          // Get dynamic filter fields
          const { bankFilterField, branchFilterField } =
            determineFilterFields(queryObject);

          // Build filters with proper bankids handling
          let shouldAddUserType = false;
          let bankIds = null;
          let userAdminRole = null;

          try {
            const userResponse = await getData(getThemeURL);
            const user = userResponse[0];
            userAdminRole = user.role_name;

            console.log(userAdminRole, "rolenameinside");

            // For SA users, check widget type to determine which parameter to use
            if (userAdminRole === "SUPER ADMIN") {
              // Check if this is an HO_bank_list widget
              if (widgetType === "HO_bank_list") {
                console.log(
                  "SA user detected - using bankids from dashboard for HO_bank_list"
                );

                // Get bankids from dashboard data parameter for HO_bank_list
                const bankids = dashboardData?.bankids;

                if (bankids !== null && bankids !== undefined) {
                  // Convert bankids to array format
                  if (Array.isArray(bankids)) {
                    bankIds = bankids.map((id) => Number(id));
                  } else if (typeof bankids === "string") {
                    bankIds = bankids.split(",").map((id) => Number(id.trim()));
                  } else if (typeof bankids === "number") {
                    bankIds = [bankids];
                  }
                }
              } else {
                console.log(
                  "SA user detected - using defaultBankId from dashboard"
                );

                // Get defaultBankId from dashboard data parameter for other widgets
                const defaultBankId = dashboardData?.defaultBankId;

                if (defaultBankId !== null && defaultBankId !== undefined) {
                  // Convert defaultBankId to array format
                  if (typeof defaultBankId === "string") {
                    bankIds = [parseInt(defaultBankId)];
                  } else if (typeof defaultBankId === "number") {
                    bankIds = [defaultBankId];
                  }
                }
              }
            } else {
              // For non-SA users, use user's bank_id as array
              console.log("Non-SA user detected - using user's bank_id");

              if (user.bank_id) {
                bankIds = [user.bank_id]; // Convert to array for consistent handling

                if (currentUserAdminRole === "U") {
                  shouldAddUserType = true;
                }
              }

              console.log("Processed bankIds for non-SA user:", bankIds);
            }
          } catch (error) {
            console.error("Error fetching user profile for filters:", error);
          }

          // Build the enhanced query with dynamic filters
          const dynamicFilters = {};

          // Add bank filter if bankIds provided and field determined
          if (bankIds && bankIds.length > 0 && bankFilterField) {
            if (bankIds.length === 1) {
              // Single value - use direct assignment
              dynamicFilters[bankFilterField] = bankIds[0];
            } else {
              // Multiple values - use IN operator
              dynamicFilters[bankFilterField] = {
                operator: "IN",
                value: bankIds,
              };
            }
          }

          // Add branch_id: null for BANK_USER type if branchFilterField exists (but not for HO_bank_list)
          if (
            shouldAddUserType &&
            branchFilterField &&
            widgetType !== "HO_bank_list"
          ) {
            dynamicFilters[branchFilterField] = null;
          }

          const enhancedQuery = {
            ...queryObject,
            filters: {
              ...queryObject.filters,
              ...dynamicFilters,
            },
          };

          // Add userType for specific cases
          if (shouldAddUserType && bankIds && bankIds.length > 0) {
            enhancedQuery.userType = "BANK_USER";
          }

          console.log(
            `Dynamic filters applied for widget ${widget.id} (${widgetType}):`,
            JSON.stringify(dynamicFilters, null, 2)
          );
          console.log(
            `User type: ${userAdminRole}, shouldAddUserType: ${shouldAddUserType}`
          );
          console.log(
            `Bank filter field: ${bankFilterField}, Branch filter field: ${branchFilterField}`
          );

          // Fetch user profile to determine auth parameters
          const userResponse = await getData(getThemeURL);
          if (
            !userResponse ||
            !Array.isArray(userResponse) ||
            userResponse.length === 0
          ) {
            console.error("Failed to fetch user profile");
            return widget;
          }

          const user = userResponse[0];
          const roleName = user.role_name;
          const userBankId = user.bank_id;

          const authObject = {
            instanceUrl: "/query-builder-api/execute",
            apiType: "POST",
          };

          if (roleName === "SUPER ADMIN") {
            authObject.type = "SA";
            // Use appropriate bank parameter based on widget type
            if (widgetType === "HO_bank_list") {
              const bankids = dashboardData?.bankids;
              if (bankids) {
                if (Array.isArray(bankids)) {
                  authObject.bank_id = bankids.map((id) => Number(id));
                } else if (typeof bankids === "string") {
                  authObject.bank_id = bankids
                    .split(",")
                    .map((id) => Number(id.trim()));
                } else {
                  authObject.bank_id = [Number(bankids)];
                }
              } else {
                authObject.bank_id = [];
              }
            } else {
              const defaultBankId = dashboardData?.defaultBankId;
              authObject.bank_id = defaultBankId ? [Number(defaultBankId)] : [];
            }
          } else if (user.is_admin === true) {
            authObject.type = "HO";
            authObject.bank_id = userBankId ? [userBankId] : [];
          } else {
            authObject.type = "U";
            authObject.bank_id = userBankId ? [userBankId] : [];
          }

          // Prepare the final payload
          const payloadWithAuth = {
            auth: authObject,
            main: enhancedQuery,
          };

          console.log(
            `Enhanced query for widget ${widget.id} (${widgetType}):`,
            JSON.stringify(payloadWithAuth)
          );

          // Make API call
          const response = await fetch("/nifi/fetchQuery-API", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payloadWithAuth),
          });

          if (!response.ok) {
            throw new Error(
              `API call failed: ${response.status} ${response.statusText}`
            );
          }

          const result = await response.json();
          console.log(`✓ API response received for widget ${widget.id}`);

          return updateWidgetWithFreshData(widget, result);
        } catch (error) {
          console.error(
            `Error refreshing data for widget ${widget.id}:`,
            error
          );
          return widget;
        }
      });

      const refreshedLayout = await Promise.all(refreshPromises);
      console.log("=== REFRESH COMPLETED ===");
      console.log("Widgets processed:", refreshedLayout.length);

      return refreshedLayout;
    } catch (error) {
      console.error("Error in refreshDashboardData:", error);
      return layout;
    }
  };
  const updateWidgetWithFreshData = (widget, apiResult) => {
    console.log("APIRESULT", apiResult);
    const freshData = apiResult.aggregatedData.data || [];

    switch (widget.type) {
      case "chart":
        return {
          ...widget,
          data: freshData,
          originalData: freshData, // Store for future reference
          lastRefreshed: new Date().toISOString(),
        };

      case "table":
        return {
          ...widget,
          data: freshData,
          originalData: freshData,
          lastRefreshed: new Date().toISOString(),
        };

      case "card":
        return {
          ...widget,
          data: freshData,
          originalData: freshData,
          lastRefreshed: new Date().toISOString(),
        };
      case "map":
        console.log("mapfreshData", freshData);
        console.log("mapwidget", widget);
        console.log("widget.dataSource", widget.dataSource);

        // Transform freshData into points array using widget's dataSource
        const points = [];

        // Handle different data structures
        if (Array.isArray(freshData)) {
          // If freshData is directly an array, we need to assign types from dataSource
          if (widget.dataSource && Array.isArray(widget.dataSource)) {
            // Use dataSource to properly type the points
            widget.dataSource.forEach((source) => {
              const fieldsConfig = widget.fieldsBySource
                ? widget.fieldsBySource[source]
                : [];

              // For each data item, create a point for this source
              freshData.forEach((dataItem) => {
                const pointData = {
                  lat: dataItem.lat || dataItem.latitude,
                  lng: dataItem.lng || dataItem.longitude,
                  type: source, // Use the actual source from dataSource array
                };

                // Add selected fields from fieldsConfig in the specified order
                fieldsConfig
                  .filter((field) => field.selected)
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .forEach((field) => {
                    pointData[field.key] = dataItem[field.key];
                  });

                points.push(pointData);
              });
            });
          } else {
            // Fallback: single dataSource or no dataSource
            const sourceType =
              (Array.isArray(widget.dataSource)
                ? widget.dataSource[0]
                : widget.dataSource) || "default";

            freshData.forEach((dataItem) => {
              const pointData = {
                lat: dataItem.lat || dataItem.latitude,
                lng: dataItem.lng || dataItem.longitude,
                type: sourceType,
              };

              // Add all available fields from the data item
              Object.keys(dataItem).forEach((key) => {
                if (
                  key !== "lat" &&
                  key !== "latitude" &&
                  key !== "lng" &&
                  key !== "longitude"
                ) {
                  pointData[key] = dataItem[key];
                }
              });

              points.push(pointData);
            });
          }
        } else {
          // Handle the original structure where freshData has sources
          for (const source in freshData) {
            const sourceData = freshData[source] || [];

            // Make sure sourceData is an array
            if (!Array.isArray(sourceData)) {
              console.warn(
                `Source data for ${source} is not an array:`,
                sourceData
              );
              continue;
            }

            const fieldsConfig = widget.fieldsBySource
              ? widget.fieldsBySource[source]
              : [];

            sourceData.forEach((dataItem) => {
              const pointData = {
                lat: dataItem.lat || dataItem.latitude,
                lng: dataItem.lng || dataItem.longitude,
                type: source,
              };

              // Add selected fields from fieldsConfig in the specified order
              fieldsConfig
                .filter((field) => field.selected)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .forEach((field) => {
                  pointData[field.key] = dataItem[field.key];
                });

              points.push(pointData);
            });
          }
        }

        console.log("Updated Map Points:", points);

        return {
          ...widget,
          data: { points }, // Same format: data contains points array
          originalData: freshData,
          lastRefreshed: new Date().toISOString(),
        };

      default:
        return {
          ...widget,
          data: freshData,
          lastRefreshed: new Date().toISOString(),
        };
    }
  };
  // Updated handleViewDashboard function
  const handleViewDashboard = async () => {
    try {
      setLoader(true);

      console.log(dashboard, "globaldashboard");

      // Get the existing dashboard data with bankids
      const dashboardData = {
        id: dashboard.id,
        title: dashboard.title || dashboard.name,
        description: dashboard.description,
        status: dashboard.status,
        category: dashboard.category,
        views: dashboard.views,
        shared: dashboard.shared,
        favorite: dashboard.favorite,
        tags: dashboard.tags,
        createdBy: dashboard.createdBy,
        updatedAt: dashboard.updatedAt,
        userId: dashboard.originalData?.userId || dashboard.userId || userId,
        parentregionid: dashboard.originalData?.parentRegionId,
        globalfillters: dashboard.originalData?.globalFiltersBuilder,
        bankids: dashboard.originalData?.bankIds || dashboard.bankIds, // Get bankids from originalData or dashboard
        defaultBankId:
          dashboard.originalData?.defaultBankId || dashboard.defaultBankId,
        icon: dashboard.originalData?.icon || dashboard.icon || "BarChart3",
        color: dashboard.originalData?.color || dashboard.color || "#1a7a73",
        layout: dashboard.originalData?.layout || dashboard.layout || [],
        attachmentList: dashboard.originalData?.attachmentList,
        lastUpdated:
          dashboard.originalData?.lastUpdated ||
          dashboard.lastUpdated ||
          dashboard.updatedAt,
      };

      console.log("Dashboard data with bankids:", dashboardData.bankids);

      // Refresh data for all widgets that have data sources
      const refreshedLayout = await refreshDashboardData(
        dashboardData.layout,
        dashboardData
      );

      console.log(refreshedLayout, "refreshedLayoutrefreshedLayout");

      // Hide loader before navigation
      setLoader(false);

      // Small delay to ensure smooth transition
      await new Promise((resolve) => setTimeout(resolve, 100));

      navigate("/dynamic-dashboard-api/Create-dashboardForApi", {
        state: {
          dashboardId: dashboard.id,
          dashboardData: {
            ...dashboardData,
            layout: refreshedLayout, // Use refreshed data instead of stored data
          },
          mode: "view",
          userId: dashboardData.userId,
          isViewing: true,
        },
      });
    } catch (error) {
      console.error("Error refreshing dashboard data:", error);
      setLoader(false);
      toast.error("Failed to load fresh data. Using cached data instead.");

      // Fallback to original behavior
      navigate("../Create-dashboardForApi", {
        state: {
          dashboardId: dashboard.id,
          mode: "view",
          isViewing: true,
        },
      });
    }
  };

  // Updated handleEditDashboard function
  const handleEditDashboard = async () => {
    if (editDisabled) return;

    console.log("dashboard items", dashboard);

    try {
      setLoader(true);

      // Get the existing dashboard data with bankids
      const dashboardData = {
        id: dashboard.id,
        title: dashboard.title || dashboard.name,
        description: dashboard.description,
        status: dashboard.status,
        category: dashboard.category,
        views: dashboard.views,
        shared: dashboard.shared,
        favorite: dashboard.favorite,
        tags: dashboard.tags,
        createdBy: dashboard.createdBy,
        updatedAt: dashboard.updatedAt,
        createdOn: dashboard.createdOn,
        userId: dashboard.originalData?.userId || dashboard.userId || userId,
        parentregionid: dashboard.originalData?.parentRegionId,
        globalfillters: dashboard.originalData?.globalFiltersBuilder,
        bankids: dashboard.originalData?.bankIds || dashboard.bankIds, // Get bankids from originalData or dashboard
        defaultBankId:
          dashboard.originalData?.defaultBankId || dashboard.defaultBankId,
        icon: dashboard.originalData?.icon || dashboard.icon || "BarChart3",
        color: dashboard.originalData?.color || dashboard.color || "#1a7a73",
        layout: dashboard.originalData?.layout || dashboard.layout || [],
        attachmentList: dashboard.originalData?.attachmentList,
        lastUpdated:
          dashboard.originalData?.lastUpdated ||
          dashboard.lastUpdated ||
          dashboard.updatedAt,
      };

      console.log("Dashboard data with bankids:", dashboardData.bankids);

      // Refresh data for all widgets that have data sources
      const refreshedLayout = await refreshDashboardData(
        dashboardData.layout,
        dashboardData
      );

      // Hide loader before navigation
      setLoader(false);

      // Small delay to ensure smooth transition
      await new Promise((resolve) => setTimeout(resolve, 100));

      navigate("/dynamic-dashboard-api/Create-dashboardForApi", {
        state: {
          dashboardId: dashboard.id,
          dashboardData: {
            ...dashboardData,
            layout: refreshedLayout, // Use refreshed data instead of stored data
          },
          mode: "edit",
          userId: dashboardData.userId,
          isEdit: true,
        },
      });
    } catch (error) {
      setLoader(false);
      toast.error("Failed to load fresh data. Using cached data instead.");

      // Fallback to original behavior if refresh fails
      const dashboardData = {
        id: dashboard.id,
        title: dashboard.title || dashboard.name,
        description: dashboard.description,
        status: dashboard.status,
        category: dashboard.category,
        views: dashboard.views,
        shared: dashboard.shared,
        favorite: dashboard.favorite,
        tags: dashboard.tags,
        createdBy: dashboard.createdBy,
        updatedAt: dashboard.updatedAt,
        createdOn: dashboard.createdOn,
        userId: dashboard.originalData?.userId || dashboard.userId || userId,
        parentregionid: dashboard.originalData?.parentRegionId,
        icon: dashboard.originalData?.icon || dashboard.icon || "BarChart3",
        color: dashboard.originalData?.color || dashboard.color || "#1a7a73",
        layout: dashboard.originalData?.layout || dashboard.layout || [],
        lastUpdated:
          dashboard.originalData?.lastUpdated ||
          dashboard.lastUpdated ||
          dashboard.updatedAt,
      };

      navigate("../Create-dashboardForApi", {
        state: {
          dashboardId: dashboard.id,
          dashboardData: dashboardData,
          mode: "edit",
          userId: dashboardData.userId,
          isEdit: true,
        },
      });
    }
  };

  const dashboardAdminRole =
    dashboard.originalData?.adminRole ||
    dashboard.dashboardAdminRole ||
    dashboard.adminRole;

  const usernames = (dashboard.bankname || "NA")
    .split(",")
    .map((u) => u.trim());
  const firstTwo = usernames.slice(0, 1);
  const remaining = usernames.length > 1 ? usernames.slice(1) : [];

  return (
    <>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div
        style={{
          background: isSelected ? "#f8fafc" : "#F9F9F9",
          borderRadius: "16px",
          padding: "24px",
          border: isSelected
            ? "2px solid #8b5cf6"
            : isDraft
            ? "2px dashed #d1d5db"
            : "1px solid #e2e8f0",
          boxShadow: isHovered
            ? "0 12px 24px rgba(0,0,0,0.12)"
            : isDraft
            ? "0 4px 12px rgba(0,0,0,0.05)"
            : "0 4px 16px rgba(0,0,0,0.06)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          transition: "all 0.2s ease",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          minHeight: "280px",
          opacity: isReadOnly ? 0.9 : 1,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Read-only indicator */}
        {isReadOnly && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "8px",
              backgroundColor: "#f59e0b",
              color: "white",
              padding: "2px 8px",
              borderRadius: "8px",
              fontSize: "10px",
              fontWeight: "600",
              zIndex: 2,
            }}
          >
            VIEW ONLY
          </div>
        )}
        {/* Selection indicator - hide in read-only mode */}
        {currentUserAdminRole !== "U" && !editDisabled && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "16px",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: isSelected ? "#8b5cf6" : "#e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(dashboard.id);
            }}
          >
            {isSelected && (
              <FaCheck style={{ color: "white", fontSize: "10px" }} />
            )}
          </div>
        )}

        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "36px",
            width: "32px",
            height: "32px",
            color: isDraft ? "#9ca3af" : "#64748b",
            backgroundColor: isDraft ? "#f9fafb" : "#f3f4f6",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
          }}
        >
          <span
            style={{
              padding: "6px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              ...(statusStyles[dashboard.status] || statusStyles["Draft"]),
            }}
          >
            <span>{statusStyles[dashboard.status]?.icon || "📝"}</span>
            {dashboard.status || "Draft"}
          </span>
        </div>

        {/* Status */}
        {/* <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <span
            style={{
              padding: "6px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              ...(statusStyles[dashboard.status] || statusStyles["Draft"]),
            }}
          >
            <span>{statusStyles[dashboard.status]?.icon || "📝"}</span>
            {dashboard.status || "Draft"}
          </span>
        </div> */}

        <div style={{ paddingTop: isReadOnly || isDraft ? "8px" : "20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                margin: "0",
                color: isDraft ? "#4b5563" : "#1f2937",
                lineHeight: "1.3",
              }}
            >
              {dashboard.title || dashboard.name}
            </h3>
          </div>

          <p
            style={{
              fontSize: "14px",
              color: isDraft ? "#6b7280" : "#6b7280",
              marginBottom: "16px",
              lineHeight: "1.4",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              cursor: "pointer", // Optional: indicates hoverable content
            }}
            title={dashboard.description} // This will show the full description on hover
          >
            {dashboard.description}
          </p>

          {/* Layout info */}
          {(dashboard.originalData?.layout || dashboard.layout) && (
            <div
              style={{
                fontSize: "12px",
                color: isDraft ? "#9ca3af" : "#8b5cf6",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              {(dashboard.originalData?.layout || dashboard.layout).length}{" "}
              widget
              {(dashboard.originalData?.layout || dashboard.layout).length !== 1
                ? "s"
                : ""}
            </div>
          )}

          {/* Tags */}
          <div
          // style={{
          //   display: "flex",
          //   flexWrap: "wrap",
          //   gap: "6px",
          //   marginBottom: "16px",
          // }}
          >
            {(dashboard.tags || []).slice(0, 2).map((tag, idx) => (
              <span
                key={idx}
                style={{
                  padding: "2px 8px",
                  borderRadius: "8px",
                  fontSize: "11px",
                  fontWeight: "500",
                  backgroundColor: isDraft ? "#f9fafb" : "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                }}
              >
                {tag}
              </span>
            ))}
            {(dashboard.tags || []).length > 2 && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "8px",
                  fontSize: "11px",
                  fontWeight: "500",
                  backgroundColor: "#f9fafb",
                  color: "#6b7280",
                }}
              >
                +{dashboard.tags.length - 2}
              </span>
            )}
          </div>

          <div
            style={{
              fontSize: "11px",
              color: "#9ca3af",
              marginBottom: "16px",
              // display: "flex",
              gap: "12px",
              // flexWrap: "wrap",
            }}
          >
            <div>
              Dashboard for:&nbsp;
              {firstTwo.join(", ")}
              {remaining.length > 0 && (
                <span
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline dotted",
                  }}
                  title={usernames.join(", ")}
                >
                  , +{remaining.length}
                </span>
              )}
            </div>
            <div>Created by: {dashboard.createdusername || "NA"}</div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                marginTop: "10px",
              }}
            >
              <div>Created on: {dashboard.createdOn || "NA"}</div>
              <div>
                Updated on:{" "}
                {dashboard.updatedAt || dashboard.lastUpdated || "NA"}
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            opacity: isHovered ? 1 : 0.8,
            transition: "opacity 0.2s ease",
          }}
        >
          <button
            style={{
              flex: 1,
              background: "linear-gradient(135deg, #1a7a73, #1a7a73)",
              border: "none",
              color: "white",
              padding: "10px",
              borderRadius: "8px",
              fontWeight: "500",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              transition: "all 0.2s ease",
              cursor: "pointer",
            }}
            onClick={handleViewDashboard}
          >
            <FaEye /> View
          </button>
          {!isReadOnly && (
            <button
              style={{
                flex: 1,
                background: editDisabled
                  ? "linear-gradient(135deg, #9ca3af, #6b7280)" // Disabled state
                  : isDraft
                  ? "linear-gradient(135deg, #6b7280, #4b5563)"
                  : "linear-gradient(135deg, #10b981, #059669)",
                border: "none",
                color: "white",
                padding: "10px",
                borderRadius: "8px",
                fontWeight: "500",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                transition: "all 0.2s ease",
                cursor: editDisabled ? "not-allowed" : "pointer",
                opacity: editDisabled ? 0.7 : 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!editDisabled) {
                  handleEditDashboard();
                }
              }}
              disabled={editDisabled}
              title={
                editDisabled
                  ? `Edit disabled - Admin role mismatch (Dashboard: ${dashboardAdminRole}, Your role: ${currentUserAdminRole})`
                  : ""
              }
            >
              <FaEdit />
              {editDisabled ? "Edit" : isDraft ? "Continue" : "Edit"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};
// Simplified Draft Section Component - No internal actions
const DraftDashboardsSection = ({
  draftDashboards,
  selectedItems,
  onSelect,
  onToggleFavorite,
  permissions,
  isExpanded,
  onToggleExpanded,
}) => {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        padding: "24px",
        marginBottom: "24px",
        border: "2px dashed #d1d5db",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        maxHeight: "600px", // Fixed maximum height
        overflow: "hidden", // Hide overflow for the main container
      }}
    >
      {/* Draft Section Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          cursor: "pointer",
          padding: "8px 0",
        }}
        onClick={onToggleExpanded}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "8px",
              backgroundColor: "#f9fafb",
              border: "2px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
            }}
          >
            <FaFileAlt style={{ fontSize: "18px" }} />
          </div>
          <div>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                margin: "0 0 2px 0",
                color: "#374151",
              }}
            >
              Draft Dashboards
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                margin: "0",
              }}
            >
              {draftDashboards.length} dashboard
              {draftDashboards.length !== 1 ? "s" : ""} in progress
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              background: "#f3f4f6",
              color: "#374151",
              padding: "8px 12px",
              borderRadius: "8px",
              fontWeight: "600",
              fontSize: "14px",
              border: "1px solid #e5e7eb",
            }}
          >
            {draftDashboards.length}
          </div>
          <div
            style={{
              color: "#6b7280",
              fontSize: "16px",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            ▼
          </div>
        </div>
      </div>

      {/* Draft Dashboards Grid */}
      {isExpanded && (
        <div
          style={{
            maxHeight: "450px", // Set height for scrollable area
            overflowY: "auto", // Enable vertical scrolling
            overflowX: "hidden", // Hide horizontal scroll
            paddingRight: "8px", // Add padding for scrollbar space
            marginRight: "-8px", // Offset padding to maintain alignment
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "20px",
            }}
          >
            {draftDashboards.map((dashboard) => (
              <DashboardCard
                key={dashboard.id}
                dashboard={dashboard}
                isSelected={selectedItems.has(dashboard.id)}
                onSelect={onSelect}
                onToggleFavorite={onToggleFavorite}
                isReadOnly={!permissions.canEdit && !permissions.canDelete}
                isDraft={true}
                // currentUserAdminRole={currentUserAdminRole}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty Draft State */}
      {isExpanded && draftDashboards.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "32px 20px",
            color: "#6b7280",
          }}
        >
          <div style={{ fontSize: "32px", marginBottom: "12px", opacity: 0.6 }}>
            📝
          </div>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "500",
              marginBottom: "6px",
              color: "#374151",
            }}
          >
            No draft dashboards
          </h3>
          <p style={{ fontSize: "14px", opacity: 0.8, margin: 0 }}>
            All your dashboards are published or saved
          </p>
        </div>
      )}
    </div>
  );
};
// Confirmation Modal and RoleSelector components remain the same...
const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "24px",
          borderRadius: "12px",
          maxWidth: "400px",
          width: "100%",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: "16px", fontSize: "18px" }}>
          Confirm
        </h3>
        <p style={{ marginBottom: "24px" }}>{message}</p>
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              backgroundColor: "white",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#ef4444",
              color: "white",
              cursor: "pointer",
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};
const RoleSelector = ({ selectedRole, onRoleChange }) => {
  const getRoleIcon = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN:
        return <FaUserShield style={{ color: "#dc2626" }} />;
      case ROLES.DISTRICT:
        return <FaMapMarkerAlt style={{ color: "#059669" }} />;
      case ROLES.USER:
        return <FaUser style={{ color: "#1a7a73" }} />;
      default:
        return <FaUser style={{ color: "#6b7280" }} />;
    }
  };

  const getSelectedOption = () => {
    if (selectedRole === ROLES.SUPER_ADMIN)
      return { type: "role", value: selectedRole };
    if (Data.includes(selectedRole))
      return { type: "district", value: selectedRole };
    if (DataForDistricts.some((user) => user.name === selectedRole))
      return { type: "user", value: selectedRole };
    return { type: "role", value: ROLES.SUPER_ADMIN };
  };

  const selectedOption = getSelectedOption();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {getRoleIcon(selectedRole)}
        <span style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}>
          Role/User:
        </span>
      </div>
      <select
        value={selectedRole}
        onChange={(e) => onRoleChange(e.target.value)}
        style={{
          padding: "12px 16px",
          paddingRight: "40px",
          borderRadius: "12px",
          border: "2px solid #e5e7eb",
          fontSize: "14px",
          fontWeight: "500",
          backgroundColor: "white",
          cursor: "pointer",
          outline: "none",
          minWidth: "180px",
          backgroundImage:
            'url(\'data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4z"/></svg>\')',
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 12px center",
          backgroundSize: "12px",
          appearance: "none",
        }}
      >
        <optgroup label="Admin Access">
          <option value={ROLES.SUPER_ADMIN}>🛡️ {ROLES.SUPER_ADMIN}</option>
        </optgroup>
        <optgroup label="Bihar Districts">
          {Data.map((district) => (
            <option key={district} value={district}>
              📍 {district}
            </option>
          ))}
        </optgroup>
        <optgroup label="Users">
          {DataForDistricts.map((user) => (
            <option key={user.name} value={user.name}>
              👤 {user.name} ({user.district})
            </option>
          ))}
        </optgroup>
      </select>
      <div
        style={{
          padding: "8px 12px",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: "600",
          backgroundColor:
            selectedOption.type === "role"
              ? "#fef2f2"
              : selectedOption.type === "district"
              ? "#f0fdf4"
              : "#f0fdfb",
          color:
            selectedOption.type === "role"
              ? "#dc2626"
              : selectedOption.type === "district"
              ? "#059669"
              : "#1a7a73",
          border: `1px solid ${
            selectedOption.type === "role"
              ? "#fecaca"
              : selectedOption.type === "district"
              ? "#bbf7d0"
              : "#99f6e4"
          }`,
        }}
      >
        {selectedOption.type === "role" && "ADMIN"}
        {selectedOption.type === "district" && "DISTRICT ADMIN"}
        {selectedOption.type === "user" && "USER"}
      </div>
    </div>
  );
};
export default function EnhancedDashboardManager() {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardSources, setDashboardSources] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(ROLES.SUPER_ADMIN);
  const [loader, setLoader] = useState(false);
  const [draftExpanded, setDraftExpanded] = useState(true);
  const [currentUserAdminRole, setCurrentUserAdminRole] = useState(null);
  const [BankId, setBankId] = useState();

  const navigate = useNavigate();
  console.log(BankId, "BankIdBankIdBankId");

  // Determine access permissions based on selected role
  const getAccessPermissions = () => {
    if (selectedRole === ROLES.SUPER_ADMIN) {
      return { canCreate: true, canEdit: true, canDelete: true, showAll: true };
    } else if (Data.includes(selectedRole)) {
      return {
        canCreate: true,
        canEdit: true,
        canDelete: true,
        showAll: false,
        district: selectedRole,
      };
    } else {
      const user = DataForDistricts.find((u) => u.name === selectedRole);
      return {
        canCreate: false,
        canEdit: false,
        canDelete: false,
        showAll: false,
        user: user?.name,
        district: user?.district,
      };
    }
  };

  const permissions = getAccessPermissions();

  // const mapRoleToAdminRole = (roleName) => {
  //   if (!roleName) return "U";

  //   const upperRole = roleName.toUpperCase();
  //   if (upperRole.includes("SUPER ADMIN")) return "SA";
  //   if (upperRole.includes("HO")) return "HO";
  //   if (upperRole.includes("BRANCH OFFICER")) return "BA";
  //   return "U";
  // };

  const mapRoleToAdminRole = (roleName, isAdmin) => {
    if (!roleName) return "U";

    const upperRole = roleName.toUpperCase();

    // Check for SUPER ADMIN first
    if (upperRole === "SUPER ADMIN") {
      return "SA";
    }

    // Check is_admin flag for HO
    if (isAdmin === true) {
      return "HO";
    }

    // If is_admin is false or undefined, return U
    return "U";
  };

  // useEffect(() => {
  useEffect(() => {
    const fetchDashboards = async () => {
      try {
        setLoading(true);

        // First, get the user data
        const userResponse = await getData(authUserData);
        console.log("User response:", userResponse);

        // Extract user information from the response
        let userId = null;
        let userRegionId = null;
        let adminRole = "U";
        let userRoleName = null; // Store the actual role name
        let userIsAdmin = false;
        let bankId = "";

        if (userResponse && userResponse.length > 0) {
          const userData = userResponse[0];
          userId = userData.id;
          userRoleName = userData.role_name; // Store the actual role name
          userIsAdmin = userData.is_admin;

          // Map role to admin_role
          adminRole = mapRoleToAdminRole(userData.role_name, userData.is_admin);
          setCurrentUserAdminRole(adminRole);

          // Get bank_id robustly (never from useState for payload)
          if (adminRole === "SA") {
            bankId = "";
          } else {
            bankId = userData.bank_id || "";
          }

          // Optionally: setBankId(bankId); // only needed if you display or use BankId elsewhere
          // Handle region_id for display/logging if desired
          if (userData.region_id && typeof userData.region_id === "string") {
            const regionIds = userData.region_id
              .split(",")
              .map((id) => id.trim());
            userRegionId = parseInt(regionIds[0]) || null;
          } else if (
            userData.region_id &&
            typeof userData.region_id === "number"
          ) {
            userRegionId = userData.region_id;
          }

          console.log("User ID:", userId);
          console.log("Admin Role:", adminRole);
          console.log("User Role Name:", userRoleName);
          console.log("Is Admin:", userIsAdmin);
          console.log("Region ID:", userRegionId);
          console.log("Bank ID:", bankId);
        }

        // Always use `bankId` from above, never use the useState BankId for the payload!
        const payload = {
          admin_role: adminRole,
          bank_id: bankId,
          user_id: userId,
        };

        console.log("API Payload:", payload);

        // Now fetch dashboards with the formatted payload
        const response = await postDataWithBody(
          getAllLayoutsByRoleForApi,
          payload,
          {}
        );

        if (response) {
          const transformedData = response.map((dashboard) => {
            let layout = [];
            try {
              if (dashboard.layoutBuilder) {
                const parsedLayout = JSON.parse(dashboard.layoutBuilder);
                layout = parsedLayout.layout || [];
              }
            } catch (e) {
              console.error("Error parsing layoutBuilder:", e);
            }

            const currentUser =
              userResponse && userResponse.length > 0 ? userResponse[0] : null;
            const assignedUser = currentUser
              ? currentUser.first_name
              : "Unknown User";
            const userAddress = currentUser ? currentUser.address : "";
            const assignedDistrict =
              Data.find((district) => userAddress.includes(district)) ||
              Data[0];

            return {
              id: dashboard.id.toString(),
              title: dashboard.title || `Dashboard ${dashboard.id}`,
              description: dashboard.description || "No description available",
              status: dashboard.status || "Draft",
              category: "General",
              views: dashboard.views || 0,
              shared: true,
              favorite: false,
              createdBy: assignedUser,
              updatedAt: dashboard.updated_on
                ? new Date(dashboard.updated_on).toLocaleDateString("en-GB")
                : "",
              createdOn: dashboard.created_on
                ? new Date(dashboard.created_on).toLocaleDateString("en-GB")
                : "",
              layout: layout,
              originalData: dashboard,
              district: assignedDistrict,
              assignedUser: assignedUser,
              regionname: dashboard.regionName,
              userId: userId,
              username: dashboard.userName,
              bankname: dashboard.bankName,
              createdusername: dashboard.createdUserName,
              regionname: dashboard.regionName,
              tags: dashboard.tags || [],
              dashboardAdminRole: dashboard.adminRole,
              userRoleName: userRoleName,
              userIsAdmin: userIsAdmin,
            };
          });

          setDashboardData(transformedData);
        } else {
          setDashboardData([]);
        }
      } catch (error) {
        console.error("Error fetching user data or dashboards:", error);
        setDashboardData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboards();
  }, []);

  //   fetchDashboards();
  // }, []);

  // Separate draft and non-draft dashboards
  // Separate draft and non-draft dashboards
  const { draftDashboards, nonDraftDashboards, filteredDashboards } =
    useMemo(() => {
      if (!dashboardData.length)
        return {
          draftDashboards: [],
          nonDraftDashboards: [],
          filteredDashboards: [],
        };

      let filtered = dashboardData.filter((d) => {
        // Super Admin can see all dashboards
        if (selectedRole === ROLES.SUPER_ADMIN) return true;

        // Get user role from dashboard data (from theme URL)
        const userRoleFromTheme = d.userRoleName?.toUpperCase();
        const userIsAdmin = d.userIsAdmin;
        const dashboardAdminRole = d.dashboardAdminRole;

        // // Map role names to admin roles for comparison
        // const mapRoleToAdminRole = (roleName) => {
        //   if (!roleName) return "U";
        //   const upperRole = roleName.toUpperCase();
        //   if (upperRole.includes("SUPER ADMIN")) return "SA";
        //   if (upperRole.includes("HO")) return "HO";
        //   if (upperRole.includes("BANK OFFICER") || upperRole.includes("BA"))
        //     return "BA";
        //   return "U";
        // };

        // Map role names to admin roles for comparison
        const mapRoleToAdminRole = (roleName, isAdmin) => {
          // ✅ UPDATED
          if (!roleName) return "U";

          const upperRole = roleName.toUpperCase();

          // Check for SUPER ADMIN first
          if (upperRole === "SUPER ADMIN") {
            return "SA";
          }

          // Check is_admin flag for HO
          if (isAdmin === true) {
            return "HO";
          }

          // If is_admin is false or undefined, return U
          return "U";
        };

        const userAdminRole = mapRoleToAdminRole(
          userRoleFromTheme,
          userIsAdmin
        );

        // If user's admin role matches dashboard's admin role, show all statuses
        if (userAdminRole === dashboardAdminRole) {
          return true; // Show Draft, Saved, and Published
        } else {
          // If roles don't match, filter out Draft and Saved - show only Published
          return d.status === "Published";
        }
      });

      // Apply search filter
      filtered = filtered.filter(
        (d) =>
          (d.name && d.name.toLowerCase().includes(search.toLowerCase())) ||
          (d.title && d.title.toLowerCase().includes(search.toLowerCase())) ||
          (d.description &&
            d.description.toLowerCase().includes(search.toLowerCase())) ||
          (d.tags &&
            d.tags.some((tag) =>
              tag.toLowerCase().includes(search.toLowerCase())
            ))
      );

      // Apply status filter to ALL dashboards, not just non-drafts
      let statusFiltered = filtered;
      if (filter !== "All") {
        statusFiltered = filtered.filter((d) => d.status === filter);
      }

      // Separate drafts and non-drafts from the status-filtered results
      const drafts = statusFiltered.filter((d) => d.status === "Draft");
      const nonDrafts = statusFiltered.filter((d) => d.status !== "Draft");

      // Sort both arrays
      const sortFunction = (a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];

        if (sortBy === "updatedAt") {
          const parseDate = (dateStr) => {
            if (!dateStr) return new Date(0);
            if (dateStr.includes("/")) {
              const parts = dateStr.split(" ")[0].split("/");
              return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
            return new Date(dateStr);
          };
          aVal = parseDate(aVal);
          bVal = parseDate(bVal);
        }

        if (sortBy === "name" || sortBy === "title") {
          const nameA = (a.name || a.title || "").toString();
          const nameB = (b.name || b.title || "").toString();
          return sortOrder === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        }

        if (sortOrder === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      };

      drafts.sort(sortFunction);
      nonDrafts.sort(sortFunction);

      return {
        draftDashboards: drafts,
        nonDraftDashboards: nonDrafts,
        filteredDashboards: statusFiltered, // All dashboards after status filtering
      };
    }, [dashboardData, filter, search, sortBy, sortOrder, selectedRole]);

  // Function to get action type and count based on selected dashboards
  // Function to get action type and count based on selected dashboards
  const getBulkActionInfo = () => {
    // Use filteredDashboards instead of combining draft and non-draft
    const allDashboards = filteredDashboards || [];
    const selectedDashboards = allDashboards.filter((d) =>
      selectedItems.has(d.id)
    );

    const draftCount = selectedDashboards.filter(
      (d) => d.status === "Draft"
    ).length;
    const savedCount = selectedDashboards.filter(
      (d) => d.status === "Saved"
    ).length;
    const publishedCount = selectedDashboards.filter(
      (d) => d.status === "Published"
    ).length;

    return {
      total: selectedDashboards.length,
      draftCount,
      savedCount,
      publishedCount,
      canSave: draftCount > 0,
      // Allow publish if there are saved dashboards (regardless of drafts)
      canPublish: savedCount > 0,
      // Flag to show when there's a mixed selection
      hasMixedSelection: draftCount > 0 && savedCount > 0,
    };
  };

  // Add this function after your existing bulk action functions
  const handleBulkPublishSavedOnly = async () => {
    if (!permissions.canEdit) return;
    if (selectedItems.size === 0) return;

    try {
      setLoader(true);

      // Get only the saved dashboards from the selection
      const selectedIds = Array.from(selectedItems);
      const savedDashboards = filteredDashboards.filter(
        (d) => selectedIds.includes(d.id) && d.status === "Saved"
      );

      if (savedDashboards.length === 0) {
        toast.info("No saved dashboards found in the selection to publish.");
        return;
      }

      const savedIds = savedDashboards.map((d) => d.id);
      await statusChangeForDashboard(savedIds, "Published");

      // Show success message with details
      toast.success(
        `Successfully published ${savedIds.length} saved dashboard${
          savedIds.length !== 1 ? "s" : ""
        }! ${
          actionInfo.draftCount > 0
            ? `${actionInfo.draftCount} draft${
                actionInfo.draftCount !== 1 ? "s" : ""
              } remain${actionInfo.draftCount === 1 ? "s" : ""} unchanged.`
            : ""
        }`,
        {
          position: "top-center",
          autoClose: 4000,
        }
      );
    } catch (error) {
      console.error("Error publishing saved dashboards:", error);
      toast.error("Failed to publish saved dashboards. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  const actionInfo = getBulkActionInfo();

  const handleSelectItem = (id) => {
    if (!permissions.canEdit) return;

    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    // Filter dashboards that actually show checkboxes
    const selectableDashboards = (filteredDashboards || []).filter(
      (dashboard) => {
        // Check if checkbox is visible for this dashboard
        const dashboardAdminRole =
          dashboard.originalData?.adminRole ||
          dashboard.dashboardAdminRole ||
          dashboard.adminRole;

        // Checkbox is hidden if user is "U" role OR if edit is disabled
        const isEditDisabled = () => {
          if (!dashboardAdminRole || !currentUserAdminRole) return false;
          return currentUserAdminRole !== dashboardAdminRole;
        };

        // Only include dashboards that show checkboxes
        return currentUserAdminRole !== "U" && !isEditDisabled();
      }
    );

    if (!permissions.canEdit || selectableDashboards.length === 0) return;

    // Check if all selectable dashboards are already selected
    const allSelectableSelected = selectableDashboards.every((d) =>
      selectedItems.has(d.id)
    );

    if (allSelectableSelected && selectableDashboards.length > 0) {
      // Deselect all
      setSelectedItems(new Set());
      setShowBulkActions(false);
    } else {
      // Select all selectable dashboards
      setSelectedItems(new Set(selectableDashboards.map((d) => d.id)));
      setShowBulkActions(true);
    }
  };

  const handleToggleFavorite = (id) => {
    if (!permissions.canEdit) return;

    const updatedData = dashboardData.map((d) =>
      d.id === id ? { ...d, favorite: !d.favorite } : d
    );
    setDashboardData(updatedData);
    updateLocalStorage(updatedData);
  };

  const handleBulkSave = () => {
    if (!permissions.canEdit) return;
    if (selectedItems.size === 0) return;
    setShowSaveModal(true);
  };

  const confirmBulkSave = async () => {
    setShowSaveModal(false);
    if (selectedItems.size === 0) return;
    const selectedIds = Array.from(selectedItems);
    await statusChangeForDashboard(selectedIds, "Saved");
  };

  const handleBulkPublish = () => {
    if (!permissions.canEdit) return;
    if (selectedItems.size === 0) return;
    setShowPublishModal(true);
  };

  const confirmBulkPublish = async () => {
    setShowPublishModal(false);
    if (selectedItems.size === 0) return;
    const selectedIds = Array.from(selectedItems);
    await statusChangeForDashboard(selectedIds, "Published");
  };

  const statusChangeForDashboard = async (
    dashboardIds,
    newStatus = "Published"
  ) => {
    try {
      setLoader(true);

      // Convert single ID to array if needed
      const idsArray = Array.isArray(dashboardIds)
        ? dashboardIds
        : [dashboardIds];

      // Filter out dashboards that are already in the target status
      const allDashboards = [...draftDashboards, ...nonDraftDashboards];
      const dashboardsToUpdate = allDashboards.filter(
        (dashboard) =>
          idsArray.includes(dashboard.id) && dashboard.status !== newStatus
      );

      if (dashboardsToUpdate.length === 0) {
        toast.info(
          `All selected dashboards are already ${newStatus.toLowerCase()}!`,
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
        setLoader(false);
        return;
      }

      // Create the payload only for dashboards that need updating
      const payload = dashboardsToUpdate.map((dashboard) => ({
        id: Number(dashboard.id),
        status: newStatus,
      }));

      console.log("Status change payload:", payload);

      // Make the API call
      const response = await postDataWithBody(statusUpdateForApi, payload, {});

      if (response && response.status) {
        // Update the local state
        const updatedData = dashboardData.map((dashboard) => {
          if (dashboardsToUpdate.some((d) => d.id === dashboard.id)) {
            return {
              ...dashboard,
              status: newStatus,
              originalData: {
                ...dashboard.originalData,
                status: newStatus,
              },
            };
          }
          return dashboard;
        });

        setDashboardData(updatedData);

        // Clear selections after successful update
        setSelectedItems(new Set());
        setShowBulkActions(false);

        const alreadyInStatusCount =
          idsArray.length - dashboardsToUpdate.length;
        let message = `Successfully updated ${
          dashboardsToUpdate.length
        } dashboard${
          dashboardsToUpdate.length !== 1 ? "s" : ""
        } to ${newStatus}!`;

        if (alreadyInStatusCount > 0) {
          message += ` (${alreadyInStatusCount} ${
            alreadyInStatusCount === 1 ? "was" : "were"
          } already ${newStatus.toLowerCase()})`;
        }

        toast.success(message, {
          position: "top-center",
          autoClose: 4000,
        });
      } else {
        throw new Error(
          response?.message || "Failed to update dashboard status"
        );
      }
    } catch (error) {
      console.error("Error updating dashboard status:", error);
      toast.error("Failed to update dashboard status. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoader(false);
    }
  };

  const updateLocalStorage = (updatedDashboards) => {
    try {
      const storageGroups = {};

      // Group dashboards by their storage source
      updatedDashboards.forEach((dashboard) => {
        const sourceKey = dashboardSources[dashboard.id];
        if (sourceKey) {
          if (!storageGroups[sourceKey]) {
            storageGroups[sourceKey] = [];
          }
          const updatedOriginalData = {
            ...dashboard.originalData,
            status: dashboard.status,
            updatedAt: dashboard.updatedAt,
            layout: dashboard.originalData?.layout || dashboard.layout || [],
          };
          storageGroups[sourceKey].push(updatedOriginalData);
        }
      });

      // Get all storage keys that had dashboards before
      const allStorageKeys = Object.values(dashboardSources);
      const uniqueStorageKeys = [...new Set(allStorageKeys)];

      // Update or remove storage keys
      uniqueStorageKeys.forEach((key) => {
        if (storageGroups[key]) {
          const dashboards = storageGroups[key];
          if (dashboards.length === 1) {
            localStorage.setItem(key, JSON.stringify(dashboards[0]));
          } else {
            localStorage.setItem(key, JSON.stringify(dashboards));
          }
        } else {
          // No dashboards left for this key, remove it
          localStorage.removeItem(key);
        }
      });

      console.log("Updated localStorage successfully");
    } catch (error) {
      console.error("Error updating localStorage:", error);
    }
  };

  const handleBulkDelete = () => {
    if (!permissions.canDelete) return;
    if (selectedItems.size === 0) return;
    setShowDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setShowDeleteModal(false);
    setLoader(true);

    if (selectedItems.size === 0) {
      setLoader(false);
      return;
    }

    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("Request timeout")), 30000); // 30 seconds timeout
    });

    try {
      const dashboardIds = Array.from(selectedItems).map((id) => Number(id));
      const payload = dashboardIds;

      console.log("Deleting dashboards with IDs:", dashboardIds);

      // Race between API call and timeout
      const response = await Promise.race([
        postDataWithBody(deleteDashboardsForApi, payload, {}),
        timeoutPromise,
      ]);

      console.log("Delete API response:", response);

      if (response && response.status) {
        const updatedData = dashboardData.filter(
          (d) => !selectedItems.has(d.id)
        );
        setDashboardData(updatedData);
        setSelectedItems(new Set());
        setShowBulkActions(false);

        toast.success(
          `Successfully deleted ${selectedItems.size} dashboard${
            selectedItems.size !== 1 ? "s" : ""
          }!`,
          {
            position: "top-center",
            autoClose: 3000,
          }
        );

        console.log(`Successfully deleted ${selectedItems.size} dashboard(s)`);
      } else {
        const errorMessage =
          response?.message || response?.error || "Failed to delete dashboards";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting dashboards:", error);

      let errorMessage =
        "An error occurred while deleting dashboards. Please try again.";

      if (error.message === "Request timeout") {
        errorMessage = "Request timed out. Please try again.";
      } else if (error.message.includes("already finished loading")) {
        errorMessage = "Request processing issue. Please try again.";
      } else if (error.message.includes("Network")) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoader(false);
    }
  };

  const handleCreateDashboard = () => {
    if (!permissions.canCreate) return;

    navigate("/dynamic-dashboard-api/Create-dashboardForApi", {
      state: {
        selectedRole: selectedRole,
        mode: "create",
        fromCreatePage: true,
        popupContext: "create", // Add this to indicate it came from Create Dashboard
      },
    });
  };

  const stats = useMemo(() => {
    const allFiltered = filteredDashboards || [];
    return {
      total: allFiltered.length,
      published: allFiltered.filter((d) => d.status === "Published").length,
      draft: allFiltered.filter((d) => d.status === "Draft").length,
      inProgress: allFiltered.filter((d) => d.status === "In Progress").length,
      saved: allFiltered.filter((d) => d.status === "Saved").length,
      favorites: allFiltered.filter((d) => d.favorite).length,
    };
  }, [filteredDashboards]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontSize: "18px",
          color: "#6b7280",
        }}
      >
        Loading dashboards...
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        {currentUserAdminRole !== "U" && (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "20px",
              marginBottom: "24px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    margin: "0 0 4px 0",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Dashboard Manager
                </h1>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    margin: "0",
                  }}
                >
                  Manage and organize your dashboards with role-based access
                  control
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "20px",
                flexWrap: "wrap",
              }}
            >
              {[
                {
                  label: "Total Dashboards",
                  value: stats.total,
                  icon: FaChartLine,
                  color: "#1a7a73",
                  bgColor: "#f0fdfb",
                },
                {
                  label: "Published",
                  value: stats.published,
                  icon: FaCheck,
                  color: "#10b981",
                  bgColor: "#f0fdf4",
                },
                {
                  label: "Drafts",
                  value: stats.draft,
                  icon: FaFileAlt,
                  color: "#6b7280",
                  bgColor: "#f9fafb",
                },
                {
                  label: "Saved",
                  value: stats.saved,
                  icon: FaBookmark,
                  color: "#f59e0b",
                  bgColor: "#fffbeb",
                },
              ].map((stat, index) => (
                <div
                  key={index}
                  style={{
                    background: stat.bgColor,
                    borderRadius: "10px",
                    padding: "12px 16px",
                    border: `1px solid ${stat.color}20`,
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    minHeight: "50px",
                    flex: "1",
                    minWidth: "150px",
                  }}
                >
                  {/* Icon Section */}
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      backgroundColor: stat.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      flexShrink: 0,
                    }}
                  >
                    <stat.icon style={{ fontSize: "14px" }} />
                  </div>

                  {/* Content Section */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: stat.color,
                        lineHeight: "1",
                        marginBottom: "2px",
                      }}
                    >
                      {stat.value}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: stat.color,
                        fontWeight: "500",
                        opacity: 0.8,
                      }}
                    >
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Controls */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "nowrap",
                  flex: 1,
                  alignItems: "center",
                  minWidth: "500px",
                }}
              >
                {/* Search */}
                <div style={{ position: "relative", minWidth: "200px" }}>
                  <FaSearch
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#9ca3af",
                      fontSize: "14px",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search dashboards..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 16px 10px 36px",
                      borderRadius: "10px",
                      border: "1px solid #e5e7eb",
                      fontSize: "14px",
                      outline: "none",
                      transition: "all 0.2s ease",
                      height: "40px",
                      boxSizing: "border-box",
                      background: "white",
                    }}
                  />
                </div>

                {/* Status Filter with Label */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* <span
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#374151",
                      minWidth: "40px",
                    }}
                  >
                    Status:
                  </span> */}
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "10px",
                      border: "1px solid #e5e7eb",
                      fontSize: "12px",
                      backgroundColor: "white",
                      cursor: "pointer",
                      outline: "none",
                      minWidth: "110px",
                      height: "40px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <option value="All">All Status</option>
                    <option value="Published">Published</option>
                    <option value="Draft">Draft</option>
                    <option value="Saved">Saved</option>
                  </select>
                </div>

                {/* Sort with Label */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* <span
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#374151",
                      minWidth: "30px",
                    }}
                  >
                    Sort:
                  </span> */}
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split("-");
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    style={{
                      padding: "10px 12px",
                      borderRadius: "10px",
                      border: "1px solid #e5e7eb",
                      fontSize: "12px",
                      backgroundColor: "white",
                      cursor: "pointer",
                      outline: "none",
                      minWidth: "130px",
                      height: "40px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <option value="updatedAt-desc">Latest First</option>
                    <option value="updatedAt-asc">Oldest First</option>
                    <option value="title-asc">Name A-Z</option>
                    <option value="title-desc">Name Z-A</option>
                    <option value="views-desc">Most Viewed</option>
                  </select>
                </div>
              </div>

              <div
                style={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                {/* Create Dashboard Button - Only show if user has create permission */}
                {permissions.canCreate && (
                  <button
                    onClick={handleCreateDashboard}
                    style={{
                      background: "linear-gradient(135deg, #10b981, #047857)",
                      color: "white",
                      border: "none",
                      padding: "10px 16px",
                      borderRadius: "10px",
                      fontWeight: "600",
                      fontSize: "14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "all 0.2s ease",
                      height: "40px",
                      boxSizing: "border-box",
                    }}
                  >
                    <FaPlus /> Create Dashboard
                  </button>
                )}

                {permissions.canEdit &&
                  (() => {
                    // Calculate selectable dashboards for button state
                    const selectableDashboards = (
                      filteredDashboards || []
                    ).filter((dashboard) => {
                      const dashboardAdminRole =
                        dashboard.originalData?.adminRole ||
                        dashboard.dashboardAdminRole ||
                        dashboard.adminRole;

                      const isEditDisabled = () => {
                        if (!dashboardAdminRole || !currentUserAdminRole)
                          return false;
                        return currentUserAdminRole !== dashboardAdminRole;
                      };

                      return currentUserAdminRole !== "U" && !isEditDisabled();
                    });

                    const allSelectableSelected = selectableDashboards.every(
                      (d) => selectedItems.has(d.id)
                    );
                    const isAllSelected =
                      allSelectableSelected && selectableDashboards.length > 0;

                    return (
                      <button
                        onClick={handleSelectAll}
                        disabled={selectableDashboards.length === 0}
                        style={{
                          background: isAllSelected
                            ? "linear-gradient(135deg, #8b5cf6, #0f4a45)"
                            : "white",
                          color: isAllSelected ? "white" : "#374151",
                          border: "1px solid #e5e7eb",
                          padding: "10px 14px",
                          borderRadius: "10px",
                          fontWeight: "600",
                          fontSize: "14px",
                          cursor:
                            selectableDashboards.length === 0
                              ? "not-allowed"
                              : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          opacity: selectableDashboards.length === 0 ? 0.5 : 1,
                          transition: "all 0.2s ease",
                          height: "40px",
                          boxSizing: "border-box",
                        }}
                      >
                        {isAllSelected ? <FaTimes /> : <FaCheck />}
                        {isAllSelected ? "Deselect All" : "Select All"}
                        {/* {selectableDashboards.length > 0 && (
        <span style={{ 
          fontSize: "12px", 
          opacity: 0.8,
          marginLeft: "4px"
        }}>
          ({selectableDashboards.length})
        </span>
      )} */}
                      </button>
                    );
                  })()}
              </div>
            </div>

            {/* Enhanced Bulk Actions - Works with all statuses */}
            {currentUserAdminRole !== "U" && showBulkActions && (
              <div
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "16px",
                  padding: "20px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "16px",
                  marginTop: "10px",
                }}
              >
                {/* Left side */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      background: "linear-gradient(135deg, #1a7a73, #1a7a73)",
                      color: "white",
                      padding: "8px 16px",
                      borderRadius: "10px",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    {actionInfo.total}{" "}
                    {actionInfo.total === 1 ? "Dashboard" : "Dashboards"}{" "}
                    Selected
                  </div>

                  {/* Status breakdown */}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      fontSize: "13px",
                      color: "#6b7280",
                    }}
                  >
                    {actionInfo.draftCount > 0 && (
                      <span>{actionInfo.draftCount} Drafts</span>
                    )}
                    {actionInfo.savedCount > 0 && (
                      <span>{actionInfo.savedCount} Saved</span>
                    )}
                    {actionInfo.publishedCount > 0 && (
                      <span>{actionInfo.publishedCount} Published</span>
                    )}
                  </div>

                  {/* Warning message for mixed selection - KEEP THIS */}
                  {actionInfo.hasMixedSelection && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#f59e0b",
                        fontWeight: "500",
                        backgroundColor: "#fffbeb",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: "1px solid #fde68a",
                      }}
                    >
                      ⚠️ Save drafts first before publishing all
                    </div>
                  )}

                  {/* Info message when only drafts are selected */}
                  {actionInfo.draftCount > 0 &&
                    actionInfo.savedCount === 0 &&
                    actionInfo.publishedCount === 0 && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#f59e0b",
                          fontWeight: "500",
                          backgroundColor: "#fffbeb",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          border: "1px solid #fde68a",
                        }}
                      >
                        ⚠️ Drafts must be saved before publishing
                      </div>
                    )}

                  <button
                    onClick={() => {
                      setSelectedItems(new Set());
                      setShowBulkActions(false);
                    }}
                    style={{
                      border: "none",
                      background: "none",
                      color: "#ef4444",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                  >
                    Clear Selection
                  </button>
                </div>

                {/* Right side (buttons) */}
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  {/* Save Drafts Button */}
                  {actionInfo.canSave && (
                    <button
                      onClick={handleBulkSave}
                      style={{
                        background: "linear-gradient(135deg, #f59e0b, #d97706)",
                        color: "white",
                        border: "none",
                        padding: "10px 20px",
                        borderRadius: "10px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Save ({actionInfo.draftCount})
                    </button>
                  )}

                  {/* Publish Buttons - Different scenarios */}

                  {/* Scenario 1: Only saved dashboards selected - Normal publish */}
                  {actionInfo.canPublish &&
                    !actionInfo.hasMixedSelection &&
                    actionInfo.draftCount === 0 && (
                      <button
                        onClick={handleBulkPublish}
                        style={{
                          background:
                            "linear-gradient(135deg, #10b981, #047857)",
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "10px",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        Publish ({actionInfo.savedCount})
                      </button>
                    )}

                  {/* Scenario 2: Only drafts selected - Disabled publish with warning */}
                  {actionInfo.draftCount > 0 &&
                    actionInfo.savedCount === 0 &&
                    actionInfo.publishedCount === 0 && (
                      <button
                        disabled
                        title="Cannot publish drafts directly. Save them first, then publish."
                        style={{
                          background:
                            "linear-gradient(135deg, #9ca3af, #6b7280)",
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "10px",
                          fontWeight: "600",
                          cursor: "not-allowed",
                          opacity: 0.7,
                        }}
                      >
                        Publish
                      </button>
                    )}

                  {/* Scenario 3: Mixed selection - Show both options */}
                  {actionInfo.hasMixedSelection && (
                    <>
                      <button
                        onClick={handleBulkPublishSavedOnly}
                        style={{
                          background:
                            "linear-gradient(135deg, #10b981, #047857)",
                          color: "white",
                          border: "none",
                          padding: "10px 20px",
                          borderRadius: "10px",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                        title={`Publish only the ${
                          actionInfo.savedCount
                        } saved dashboard${
                          actionInfo.savedCount !== 1 ? "s" : ""
                        }, leave drafts unchanged`}
                      >
                        Publish ({actionInfo.savedCount})
                      </button>

                      {/* <button
            onClick={async () => {
              // Save drafts first, then publish all
              await handleBulkSave();
              // Wait a moment for the save to complete, then publish
              setTimeout(() => {
                handleBulkPublish();
              }, 1500);
            }}
            style={{
              background: "linear-gradient(135deg, #8b5cf6, #0f4a45)",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "10px",
              fontWeight: "600",
              cursor: "pointer",
            }}
            title="Save all drafts first, then publish everything"
          >
            Save & Publish All
          </button> */}
                    </>
                  )}

                  {/* Delete Button */}
                  <button
                    onClick={handleBulkDelete}
                    style={{
                      background: "linear-gradient(135deg, #ef4444, #dc2626)",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "10px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    Delete ({actionInfo.total})
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {currentUserAdminRole == "U" && (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "20px",
              marginBottom: "24px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "16px",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              {/* Left section: title + subtitle */}
              <div style={{ flex: "1" }}>
                <h1
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    margin: "0 0 4px 0",
                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Published Dashboards
                </h1>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    margin: "0",
                  }}
                >
                  Dashboards for the Users
                </p>
              </div>

              {/* Right section: search input */}
              <div
                style={{
                  position: "relative",
                  minWidth: "160px",
                  flexShrink: 0,
                }}
              >
                <FaSearch
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9ca3af",
                    fontSize: "12px",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px 6px 28px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    fontSize: "12px",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    height: "32px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Draft Dashboards Section - Simplified without internal actions */}
        {draftDashboards.length > 0 && (
          <DraftDashboardsSection
            draftDashboards={draftDashboards}
            selectedItems={selectedItems}
            onSelect={handleSelectItem}
            onToggleFavorite={handleToggleFavorite}
            permissions={permissions}
            isExpanded={draftExpanded}
            onToggleExpanded={() => setDraftExpanded(!draftExpanded)}
            currentUserAdminRole={currentUserAdminRole}
          />
        )}

        {/* Published/Saved Dashboards Section */}
        {nonDraftDashboards.length > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              marginBottom: "24px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
              border: "1px solid #f1f5f9",
              maxHeight: "700px", // Set maximum height
              overflow: "hidden", // Hide overflow for main container
            }}
          >
            <div style={{ marginBottom: "20px" }}>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#374151",
                  margin: "0 0 8px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#059669",
                  }}
                >
                  <FaCheck style={{ fontSize: "16px" }} />
                </div>
                Published & Saved Dashboards
              </h2>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  margin: "0",
                }}
              >
                {nonDraftDashboards.length} dashboard
                {nonDraftDashboards.length !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Scrollable Dashboard Grid */}
            <div
              style={{
                maxHeight: "500px", // Set height for scrollable area
                overflowY: "auto", // Enable vertical scrolling
                overflowX: "hidden", // Hide horizontal scroll
                paddingRight: "8px", // Add padding for scrollbar space
                marginRight: "-8px", // Offset padding to maintain alignment
              }}
            >
              {/* Dashboard Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {nonDraftDashboards.map((dashboard) => (
                  <DashboardCard
                    key={dashboard.id}
                    dashboard={dashboard}
                    isSelected={selectedItems.has(dashboard.id)}
                    onSelect={handleSelectItem}
                    onToggleFavorite={handleToggleFavorite}
                    isReadOnly={!permissions.canEdit && !permissions.canDelete}
                    currentUserAdminRole={currentUserAdminRole}
                    dashboardData={dashboardData}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {(filteredDashboards || []).length === 0 && (
          <div
            style={
              currentUserAdminRole !== "U"
                ? {
                    background: "white",
                    borderRadius: "16px",
                    padding: "48px 32px",
                    textAlign: "center",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    border: "1px solid #f1f5f9",
                    // height: "500px"
                  }
                : {
                    background: "white",
                    borderRadius: "16px",
                    padding: "160px 32px",
                    textAlign: "center",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    border: "1px solid #f1f5f9",
                    height: "500px",
                  }
            }
          >
            <div
              style={{
                fontSize: "40px",
                marginBottom: "12px",
                opacity: 0.5,
              }}
            >
              📊
            </div>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#374151",
                marginBottom: "6px",
              }}
            >
              No dashboards found
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                marginBottom: "20px",
              }}
            >
              {currentUserAdminRole !== "U"
                ? search
                  ? "Try adjusting your search terms or filters"
                  : ""
                  ? "Create your first dashboard to get started"
                  : "No dashboards are available for your current role"
                : "No published dashboards available for your user"}
            </p>
          </div>
        )}
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to delete ${
          selectedItems.size
        } selected dashboard${
          selectedItems.size !== 1 ? "s" : ""
        }? This action cannot be undone.`}
      />

      <ConfirmationModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={confirmBulkSave}
        message={`Are you sure you want to save ${
          actionInfo.draftCount
        } draft dashboard${
          actionInfo.draftCount !== 1 ? "s" : ""
        }? They will be moved to saved status.`}
      />

      <ConfirmationModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={confirmBulkPublish}
        message={`Are you sure you want to publish ${
          actionInfo.total - actionInfo.publishedCount
        } selected dashboard${
          actionInfo.total - actionInfo.publishedCount !== 1 ? "s" : ""
        }? They will be made available to all users.`}
      />
    </div>
  );
}
